import { useState } from 'react';
import type { ChatMessage } from '../../types/coach';
import Button from '../atoms/Button';
import Badge from '../atoms/Badge';

interface InternalMessageProps {
  message: ChatMessage;
}

export function InternalMessage({ message }: InternalMessageProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!message.internal || !message.content) {
    return null;
  }

  const isToolUse = message.role === 'tool-use';
  const isToolResult = message.role === 'tool-result';
  
  // For tool-result, show only first 100 characters when collapsed
  const previewText = isToolResult && !isExpanded 
    ? message.content.slice(0, 100) + (message.content.length > 100 ? '...' : '')
    : message.content;

  const showExpandButton = isToolResult && message.content.length > 100;

  return (
    <div className="message-bubble assistant p-4 w-full">
      <div className="space-y-3 text-text-primary">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Badge 
              variant={isToolUse ? 'info' : 'warning'} 
              size="xs"
              icon={
                <div className={`w-2 h-2 rounded-full ${
                  isToolUse ? 'bg-info' : 'bg-warning'
                }`} />
              }
            >
              {isToolUse ? 'Tool Use' : 'Tool Result'}
            </Badge>
          </div>
          
          {showExpandButton && (
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="neutral"
              size="xsmall"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          )}
        </div>

        {/* Content */}
        <div className="text-text-primary leading-relaxed">
          {isToolUse ? (
            // Tool use: show formatted YAML/JSON
            <pre className="whitespace-pre-wrap break-words text-sm font-mono bg-bg-surface p-3 rounded-md border border-border-light overflow-x-auto">
              {previewText}
            </pre>
          ) : (
            // Tool result: show as expandable text
            <div className="whitespace-pre-wrap break-words text-sm bg-bg-surface p-3 rounded-md border border-border-light">
              {previewText}
            </div>
          )}
        </div>

        {/* Expand indicator for tool-result */}
        {showExpandButton && !isExpanded && (
          <div className="text-text-muted text-xs italic">
            Click &quot;Expand&quot; to see full response ({message.content.length} characters)
          </div>
        )}
      </div>
    </div>
  );
}